import type { GameHistoryItem } from '@wx-weiqi/contracts';

function gameDate(game: GameHistoryItem): string {
  return new Date(game.startedAt ?? game.finishedAt).toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

interface PlayerGamePickerDialogProps {
  readonly games: readonly GameHistoryItem[];
  readonly currentGameId: string | null;
  readonly pending: boolean;
  readonly error: string | null;
  readonly onClose: () => void;
  readonly onRefresh: () => void;
  readonly onSelect: (gameId: string) => void;
}

export function PlayerGamePickerDialog({
  games,
  currentGameId,
  pending,
  error,
  onClose,
  onRefresh,
  onSelect,
}: PlayerGamePickerDialogProps) {
  return (
    <section
      className="fox-kifu-details-backdrop teacher-game-picker-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.currentTarget === event.target && !pending) onClose();
      }}
    >
      <div
        className="fox-kifu-details-dialog teacher-game-picker-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="player-game-picker-title"
      >
        <header>
          <span>
            <small>我的对局记录</small>
            <h2 id="player-game-picker-title">对局列表</h2>
          </span>
          <button type="button" disabled={pending} onClick={onClose}>
            关闭
          </button>
        </header>
        <div className="teacher-game-picker-content" aria-live="polite" aria-busy={pending}>
          <div className="teacher-game-picker-heading">
            <span>对局日期</span>
            <span>黑方</span>
            <span>白方</span>
            <span>棋局</span>
            <span>操作</span>
          </div>
          {error !== null ? <p className="message message--error">{error}</p> : null}
          {games.length === 0 && !pending && error === null ? (
            <p className="history-empty">暂无可查看的对局记录。</p>
          ) : null}
          <div className="teacher-game-picker-list">
            {games.map((game) => {
              const black = game.players.find((player) => player.color === 'BLACK');
              const white = game.players.find((player) => player.color === 'WHITE');
              const selected = game.gameId === currentGameId;
              return (
                <div className="teacher-game-picker-row" key={game.gameId}>
                  <button
                    className={`teacher-game-picker-select${selected ? ' is-selected' : ''}`}
                    type="button"
                    aria-current={selected ? 'true' : undefined}
                    disabled={pending}
                    onClick={() => {
                      if (selected) onClose();
                      else onSelect(game.gameId);
                    }}
                  >
                    <span data-label="对局日期">{gameDate(game)}</span>
                    <span data-label="黑方">{black?.displayName ?? '—'}</span>
                    <span data-label="白方">{white?.displayName ?? '—'}</span>
                    <span data-label="棋局">
                      {game.boardSize} 路 · {game.currentSequence} 手
                    </span>
                    <strong data-label="操作">{selected ? '当前棋谱' : '切换棋谱'}</strong>
                  </button>
                </div>
              );
            })}
          </div>
          {pending ? <p className="teacher-game-picker-loading">正在读取我的棋谱…</p> : null}
        </div>
        <footer>
          <span>共 {games.length} 盘对局</span>
          <div>
            <button type="button" disabled={pending} onClick={onRefresh}>
              {pending ? '正在刷新…' : '刷新列表'}
            </button>
            <button type="button" disabled={pending} onClick={onClose}>
              关闭
            </button>
          </div>
        </footer>
      </div>
    </section>
  );
}
